/* eslint-disable no-param-reassign */
import { FC } from 'react';
import { isNull, isUndefined } from 'lodash';
import { range } from 'mathjs';
import { ONE_HUNDRED_VALUE } from 'common/config/app';
import { ENTERPRISE_VALUE_ALIAS } from 'common/constants/allocations';
import { LAST_TWELVE_MONTHS, LTM, NEXT_TWELVE_MONTHS, NTM } from 'common/constants/financials';
import { MAX_CHAR_LENGTH } from 'common/constants/general';
import { GRID_NUMBER_CHECKBOX, NUMBER, PERCENTAGE, STRING } from 'common/constants/gridType';
import { ENTER_DATA } from 'common/constants/inputs';
import { currencyFormat, largeCurrencyFormat, twoDecimalPercentFormat } from 'common/formats/formats';
import { GridDateProps, GridType } from 'common/types/scalarSpreadsheet';
import { SelectValueViewer } from 'components';
import { GridDate, GridDateEditor, GridDateValueViewer, GridSelect } from 'components/FeaturedSpreadsheet/components';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  BENCHMARK_ALIASES,
  LTM_EBITDA_CUSTOM_KEY,
  LTM_REVENUE_CUSTOM_KEY,
  NTM_EBITDA_CUSTOM_KEY,
  NTM_REVENUE_CUSTOM_KEY,
  PERCENTILE_ROWS,
  SELECTED_MULTIPLE,
  SELECTION,
  WEIGHTED_EQUITY_VALUE,
  WEIGHTED_EV,
  WEIGHTING,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import {
  addBenchmarkCustomKeys,
  getSelectionValue,
  handleMultipleCell,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/config/utilities';
import handleOptionsNumberFormat from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/handleOptionsNumberFormat';
import {
  ACQUIRER_NAME_ID,
  ACQUIRER_NAME_LETTER,
  ACQUIRER_NAME_TITLE,
  CAP_IQ_TRANSACTION_ID,
  CAP_IQ_TRANSACTION_ID_LETTER,
  CAP_IQ_TRANSACTION_ID_TITLE,
  ENTERPRISE_VALUE_LETTER,
  GPC_MULTIPLE_DISCOUNT_ALIAS,
  GPC_NTM_EBITDA_APPROACH_ALIAS,
  GPC_NTM_REVENUE_APPROACH_ALIAS,
  GPT_SUMMARY,
  LESS_DEBT_ALIAS,
  LTM_COLUMNS,
  LTM_EBITDA_LETTER,
  LTM_EBITDA_MULTIPLE_ID,
  LTM_EBITDA_MULTIPLE_LETTER,
  LTM_MULTIPLE_COLUMNS_ALIAS,
  LTM_REVENUE_LETTER,
  LTM_REVENUE_MULTIPLE_LETTER,
  MULTIPLE_COLUMNS_ALIAS,
  MULTIPLE_COLUMNS_LETTER,
  MULTIPLE_DISCOUNT_ALIAS,
  MULTIPLE_DISCOUNT_ROW_NUMBER_OFFSET,
  NTM_EBITDA_COLUMN_ID,
  NTM_EBITDA_MULTIPLE_ID,
  NTM_EBITDA_MULTIPLE_LETTER,
  NTM_MULTIPLES_IDS,
  NTM_REVENUE_COLUMN_ID,
  NTM_REVENUE_MULTIPLE_LETTER,
  PLUS_CASH_ALIAS,
  TRANSACTION_DATE_ID,
  TRANSACTION_DATE_LETTER,
} from 'pages/ValuationsAllocation/approaches/TransactionComps/config/constants';
import {
  CellParser,
  CompleteTransactionCellParams,
  ReplaceColumnLegendInRowRange,
  ReverseParsedTransaction,
  ReverseParsedValuationApproachGPT,
  TableDataMultipleAndWeight,
  TableDataMultiplesAndWeightings,
  TransactionCompsCell,
  TransactionCompsColumn,
  TransactionCompsConfigurationRow,
  TransactionCompsParser,
} from 'pages/ValuationsAllocation/approaches/TransactionComps/types';
import {
  COMPANY_ROW_ALIAS,
  VALUATIONS_SPREADSHEET_ENTERPRISE_VALUE_KEY,
  VALUATIONS_SPREADSHEET_EQUITY_VALUE_KEY,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { getApproachTableName, getEbitdaValueForViewer, getLtmNtmEBITDAs } from 'pages/ValuationsAllocation/util';
import {
  getArrayValue,
  getStringValue,
  getValueByPropOrFallback,
  gridShortDate,
  isValidDate,
  parseValue,
} from 'utilities';
import { alphabetGenerator, getColumnIndex, getColumnLetter } from 'utilities/alphabet-utilities';
import { shouldIgnoreExpr } from 'utilities/shouldIgnoreExpr';
import getNtmDiscount from './getNtmDiscount';

function getExpr(row: TransactionCompsConfigurationRow, columnLegend: string) {
  return row.expr ? row.expr.replace(/@/g, columnLegend) : '';
}

const replaceColumnLegendInRowRange: ReplaceColumnLegendInRowRange = params => {
  const { rowRange, columnLegend, offset = 2 } = params;
  const regex = new RegExp(columnLegend, 'g');
  const currentColumnNumber = getColumnIndex(columnLegend);
  const referencedColumnLegend = getColumnLetter((currentColumnNumber as number) - offset);
  const updatedRowRange = rowRange.replace(regex, referencedColumnLegend);
  return updatedRowRange;
};

const generateDiscountedExpression = (cell: TransactionCompsCell, rowRange: string) => {
  const { columnLegend } = cell;
  const columnLegendString = getStringValue(columnLegend);
  const multipleDiscountRowNumber = rowRange.split(',').length + MULTIPLE_DISCOUNT_ROW_NUMBER_OFFSET;
  const multipleDiscountKey = `${columnLegend}${multipleDiscountRowNumber}`;
  const updatedRowRange = replaceColumnLegendInRowRange({ rowRange, columnLegend: columnLegendString });
  const discountedExpression =
    rowRange?.length > 2 ? `${cell.expr?.replace(rowRange, updatedRowRange)} * (1 + ${multipleDiscountKey})` : '';
  return discountedExpression;
};

const completeTransactionCell = (params: CompleteTransactionCellParams) => {
  /* Gives extra props according to nature of cell (columnId) */
  const { cell, row, columnId } = params;
  if (columnId === TRANSACTION_DATE_ID) {
    return {
      ...cell,
      readOnly: row.readOnly,
      gridDateComponent: GridDate as FC<GridDateProps>,
      dataEditor: (props: any) => <GridDateEditor {...props} />,
      valueViewer: (props: any) => <GridDateValueViewer {...props} />,
    };
  }
  if (columnId === CAP_IQ_TRANSACTION_ID) {
    return {
      ...cell,
      placeholder: ENTER_DATA,
      readOnly: row.readOnly,
      isRequired: false,
    };
  }
  if (columnId === ACQUIRER_NAME_ID) {
    return {
      ...cell,
      placeholder: ENTER_DATA,
      maxNumberChars: MAX_CHAR_LENGTH,
      isParent: true,
    };
  }
  if (NTM_MULTIPLES_IDS.includes(cell.columnId as string)) {
    // remove checkbox from NTM multiples in transaction rows
    return {
      ...cell,
      valueViewer: null,
      forceComponent: false,
      gridType: STRING as GridType,
      expr: null,
      value: '',
      isRequired: false,
      readOnly: true,
    };
  }
  return cell;
};

function generatePercentilesExpression(cell: TransactionCompsCell, rowRange: string) {
  if (LTM_MULTIPLE_COLUMNS_ALIAS.includes(cell.columnId as string)) {
    return `=PERCENTILE(FILTER_ENABLED(${rowRange}), TITLES_${cell.alias})`;
  }
  // fix row range and add discount factor
  const { columnLegend } = cell;
  const columnLegendString = getStringValue(columnLegend);
  const multipleDiscountRowNumber = rowRange.split(',').length + MULTIPLE_DISCOUNT_ROW_NUMBER_OFFSET;
  const multipleDiscountKey = `${columnLegend}${multipleDiscountRowNumber}`;
  const updatedRowRange = replaceColumnLegendInRowRange({ rowRange, columnLegend: columnLegendString });
  return `=PERCENTILE(FILTER_ENABLED(${updatedRowRange}), TITLES_${cell.alias}) * (1 + ${multipleDiscountKey})`;
}

const updatePercentileCellProps = ({ cell, rowRange }: { rowRange: string; cell: TransactionCompsCell }) => {
  const updatedCell = { ...cell };
  if (MULTIPLE_COLUMNS_ALIAS.includes(cell.columnId as string)) {
    const percentileExpr = generatePercentilesExpression(cell, rowRange);
    updatedCell.expr = percentileExpr;
  }
  updatedCell.readOnly = true;
  delete updatedCell.dataEditor;
  delete updatedCell.valueViewer;
  delete updatedCell.isEditableTitleCell;
  delete updatedCell.useScalarSpreadsheetCell;
  delete updatedCell.dropdown;
  return updatedCell;
};

function getVal(column: TransactionCompsColumn, row: TransactionCompsConfigurationRow) {
  let value = null;
  const columnProps = column[row.alias];

  if (row.gridType === GRID_NUMBER_CHECKBOX) {
    const columnId = column.row_ref || column.id;
    value = columnProps?.number || columnProps?.[columnId];
    if (value && isValidDate(value)) {
      value = gridShortDate(value);
    }
  } else {
    value = columnProps;
  }
  return !Number.isNaN(value) ? value : null;
}

const getValue = (
  column: TransactionCompsColumn,
  row: TransactionCompsConfigurationRow,
  specificApproach: ReverseParsedValuationApproachGPT
) => {
  if (row.alias === SELECTION) {
    return getSelectionValue(row, column, specificApproach);
  }
  if (row.alias === MULTIPLE_DISCOUNT_ALIAS && column.id) {
    const metricKey = column.id.split('_')[1];
    const propertyToRead = `ntm_${metricKey}_discount`;
    return specificApproach[propertyToRead as keyof ReverseParsedValuationApproachGPT];
  }

  // The company rows have object data types, both a number and an enabled status,
  // and the below code keeps the cellReferenceBar from breaking
  return getVal(column, row);
};

const getValueToUse = (
  columnValue: TransactionCompsCell['value'],
  tempValue: TransactionCompsCell['value'],
  preferenceForTemp: boolean
) => {
  if (preferenceForTemp && tempValue) {
    return tempValue;
  }
  return tempValue && isNull(columnValue) ? tempValue : columnValue;
};

const checkForTempData = (
  column: TransactionCompsColumn,
  row: TransactionCompsConfigurationRow,
  transactionsInTable: ReverseParsedValuationApproachGPT['gpt_transactions'] | undefined,
  multiplesAndWeightings: TableDataMultipleAndWeight,
  isRelevantRow: boolean,
  specificApproach: ReverseParsedValuationApproachGPT
) => {
  if (multiplesAndWeightings && isRelevantRow) {
    if (row.alias === SELECTION) {
      return getSelectionValue(row, column, specificApproach);
    }
    if (row.alias === WEIGHTING) {
      return Number(multiplesAndWeightings[row.alias]) * ONE_HUNDRED_VALUE;
    }
    return multiplesAndWeightings[row.alias as keyof TableDataMultipleAndWeight];
  }
  // in column, row doesn't have data, but in tableData it has information
  const propInRow = column.id;
  const matchedRow = transactionsInTable?.find(({ row_ref }) => row.row_ref === row_ref);
  const tempValue = matchedRow?.[propInRow as keyof ReverseParsedTransaction];
  return !Number.isNaN(tempValue) ? tempValue : null;
};

const updateMultipleCell = (
  cell: TransactionCompsCell,
  rowNumber: number,
  columnLegend: string,
  columns: TransactionCompsColumn[]
) => {
  const enterpriseValueItem = columns.find(column => column.id === ENTERPRISE_VALUE_ALIAS) as TransactionCompsColumn;
  const enterpriseValueCell = enterpriseValueItem[cell.alias];
  const enterpriseValue = enterpriseValueCell[ENTERPRISE_VALUE_ALIAS];
  const { value, enabled } = cell;
  const isEmptyCells = isUndefined(value) && isNull(enterpriseValue);
  const expr =
    columnLegend === LTM_REVENUE_MULTIPLE_LETTER
      ? `=${ENTERPRISE_VALUE_LETTER}${rowNumber}/${LTM_REVENUE_LETTER}${rowNumber}`
      : `=${ENTERPRISE_VALUE_LETTER}${rowNumber}/${LTM_EBITDA_LETTER}${rowNumber}`;
  if (!isEmptyCells) {
    const ignoreExpr = shouldIgnoreExpr(value);
    cell.ignoreExpr = ignoreExpr;
    cell.enabled = enabled && !ignoreExpr;
  }
  cell.expr = expr;
};

const cellParser: CellParser = params => {
  let { cell } = params;
  const {
    column,
    columns,
    columnLegend,
    existAliasAndIsNotTitle,
    expr,
    isDisabled,
    inLTM,
    inMultipleCell,
    row,
    rowNumber,
    rowRange,
    type,
    value,
  } = params;
  const cellColumnId = cell.columnId as string;

  if (columnLegend === ENTERPRISE_VALUE_LETTER && existAliasAndIsNotTitle) {
    cell = {
      ...cell,
      gridType: NUMBER,
      format: largeCurrencyFormat,
    };
  } else if (inLTM && existAliasAndIsNotTitle) {
    cell = {
      ...cell,
      gridType: NUMBER,
      format: largeCurrencyFormat,
      expr: expr(),
      value: parseValue(value, type, null, null, row.dbType),
    };
  } else if (inMultipleCell && cell.alias !== 'multiple_discount') {
    cell = {
      ...row,
      ...cell,
      expr: expr(),
      value: parseValue(value, type, 0, null, row.dbType),
    } as Omit<ICell, 'options' | 'sheet'>;
  } else if (NTM_MULTIPLES_IDS.includes(cellColumnId) && cell.alias === MULTIPLE_DISCOUNT_ALIAS) {
    cell = {
      ...cell,
      gridType: PERCENTAGE,
      readOnly: isDisabled,
      format: twoDecimalPercentFormat({ minimumFractionDigits: 0 }),
      defaultValue: 0,
      isExpr: false,
      value,
      dbDecimalPlaces: 4, // this would be better to have it read from field attributes
    };
  }

  if (cell.isMultipleCell) {
    cell.customKey = `${column.fields[0].number}_${cell.alias}`;
  }
  if (NTM_MULTIPLES_IDS.includes(cellColumnId) && BENCHMARK_ALIASES.includes(cell.alias) && cell.expr) {
    // replace row range and apply factor
    const discountedExpression = generateDiscountedExpression(cell, rowRange);
    cell = {
      ...cell,
      expr: discountedExpression,
    };
  }
  if (
    typeof column[row.alias] === 'object' &&
    !isNull(column[row.alias]) &&
    inMultipleCell &&
    !NTM_MULTIPLES_IDS.includes(cellColumnId)
  ) {
    cell.enabled = column[row.alias].enabled;
    cell.revenue = column[row.alias].number;
    updateMultipleCell(cell, rowNumber, columnLegend, columns);
  }

  addBenchmarkCustomKeys(cell, cell, column);

  if (row.isGptRow) {
    const columnId = getValueByPropOrFallback(column, 'row_ref', column.id);
    cell = {
      ...cell,
      isRequired: true,
      readOnly: isDisabled || row.readOnly,
      isGptRow: row.isGptRow,
    };
    cell = completeTransactionCell({ cell, row, columnId });
  }

  const isPercentileCell = PERCENTILE_ROWS.includes(cell.alias);
  if (isPercentileCell) {
    cell = updatePercentileCellProps({ cell, rowRange });
  }

  return cell;
};

const parser: TransactionCompsParser = async ({ columns, rowConfig, tableData }) => {
  const sourceData = tableData.approach;
  const {
    financials: {
      total_cash_equivalents: plusCash,
      total_debt: lessDebt,
      ltm_ebitda: ltmEbitda,
      ltm_adjusted_ebitda: ltmAdjustedEbitda,
      ntm_ebitda: ntmEbitda,
      ntm_adjusted_ebitda: ntmAdjustedEbitda,
      use_adjusted_ebitda: useFinancialStatementAdjustedEbitda,
    },
    isDisabled,
  } = tableData;
  const financialsPeriods = [
    {
      period_type: LTM,
      income_statement: {
        ebitda: ltmEbitda.toString(),
        adjusted_ebitda: ltmAdjustedEbitda.toString(),
      },
    },
    {
      period_type: NTM,
      income_statement: {
        ebitda: ntmEbitda.toString(),
        adjusted_ebitda: ntmAdjustedEbitda.toString(),
      },
    },
  ];
  const ebitdaOptions = getLtmNtmEBITDAs(financialsPeriods, true);

  const {
    gpt_transactions: tableDataTransactions,
    ltm_revenue_selection: revenueSelection,
    ltm_ebitda_selection: ebitdaSelection,
    ltm_ebitda_weighting: ebitdaWeighting,
    ltm_revenue_weighting: revenueWeighting,
    ltm_ebitda_selected_multiple: ebitdaSpecifiedMultiple,
    ltm_revenue_selected_multiple: revenueSpecifiedMultiple,
    ntm_ebitda_gpc_approach: ntmEbitdaGpcApproach,
    ntm_revenue_gpc_approach: ntmRevenueGpcApproach,
    ntm_revenue_gpc_reference: ntmRevenueGpcReference,
    ntm_ebitda_gpc_reference: ntmEbitdaGpcReference,
  } = sourceData.valuations_approach_gpt;

  const tableDataMultiplesAndWeightings: TableDataMultiplesAndWeightings = {
    [LTM_REVENUE_MULTIPLE_LETTER]: {
      selection: revenueSelection,
      weighting: revenueWeighting,
      selected_multiple: revenueSpecifiedMultiple,
    },
    [LTM_EBITDA_MULTIPLE_LETTER]: {
      selection: ebitdaSelection,
      weighting: ebitdaWeighting,
      selected_multiple: ebitdaSpecifiedMultiple,
    },
  };

  // These default cells consist of some default cell headers
  let cells: Record<string, TransactionCompsCell> = {
    G1: {
      key: 'G1',
      className: 'text-center division-bottom-only',
      value: LAST_TWELVE_MONTHS,
      readOnly: true,
      colSpan: 2,
      rowSpan: 1,
      alias: '',
    },
    I1: {
      key: 'I1',
      className: 'text-center division-bottom-only',
      value: NEXT_TWELVE_MONTHS,
      readOnly: true,
      colSpan: 2,
      rowSpan: 1,
      alias: '',
    },
    C1: {
      key: 'C1',
      className: 'text-center division-bottom-only',
      value: 'TRANSACTION DETAILS',
      readOnly: true,
      colSpan: 4,
      rowSpan: 1,
      alias: '',
    },
    A1: {
      key: 'A1',
      className: 'division-bottom-only',
      value: CAP_IQ_TRANSACTION_ID_TITLE,
      readOnly: true,
      colSpan: 1,
      rowSpan: 2,
      alias: '',
      parentColumn: ACQUIRER_NAME_ID,
    },
    B1: {
      key: 'B1',
      className: 'division-bottom-only',
      value: ACQUIRER_NAME_TITLE,
      readOnly: true,
      colSpan: 1,
      rowSpan: 2,
      alias: '',
      shouldDisplayTooltip: true,
      isParent: true,
      columnId: ACQUIRER_NAME_ID,
    },
  };

  const weightingRow = rowConfig.length - 4;
  const valueRow = rowConfig.length - 5;
  const weightedEquityValueTableRowNumber = rowConfig.length;
  const weightedEnterpriseValueTableRowNumber = weightedEquityValueTableRowNumber - 3;
  const cashTableRowNumber = weightedEquityValueTableRowNumber - 2;
  const debtTableRowNumber = weightedEquityValueTableRowNumber - 1;

  function getExpressionPiece(legend: string) {
    return `${legend}${weightingRow}/100 * ${legend}${valueRow}`;
  }

  cells[`A${weightedEnterpriseValueTableRowNumber}`] = {
    key: `A${weightedEnterpriseValueTableRowNumber}`,
    colSpan: 10,
    alias: WEIGHTED_EV,
    columnLegend: 'A',
    gridType: NUMBER,
    customKey: VALUATIONS_SPREADSHEET_ENTERPRISE_VALUE_KEY,
    expr: `=${MULTIPLE_COLUMNS_LETTER.map(legend => getExpressionPiece(legend)).join(' + ')}`,
    format: currencyFormat({ fractionDigits: 2 }),
    className: 'subtitle large-cell-value',
  };
  cells[`A${cashTableRowNumber}`] = {
    key: `A${cashTableRowNumber}`,
    colSpan: 10,
    alias: PLUS_CASH_ALIAS,
    value: plusCash,
    columnLegend: 'A',
    gridType: NUMBER,
    format: currencyFormat({ fractionDigits: 2 }),
    className: 'large-cell-value',
  };
  cells[`A${debtTableRowNumber}`] = {
    key: `A${debtTableRowNumber}`,
    colSpan: 10,
    alias: LESS_DEBT_ALIAS,
    value: lessDebt,
    columnLegend: 'A',
    gridType: NUMBER,
    format: currencyFormat({ fractionDigits: 2 }),
    className: 'large-cell-value',
  };
  cells[`A${weightedEquityValueTableRowNumber}`] = {
    key: `A${weightedEquityValueTableRowNumber}`,
    colSpan: 10,
    alias: WEIGHTED_EQUITY_VALUE,
    columnLegend: 'A',
    gridType: NUMBER,
    customKey: VALUATIONS_SPREADSHEET_EQUITY_VALUE_KEY,
    expr: `=A${weightedEnterpriseValueTableRowNumber} + A${cashTableRowNumber} - A${debtTableRowNumber}`,
    format: currencyFormat({ fractionDigits: 2 }),
    className: 'subtitle large-cell-value',
  };

  const alphabet = alphabetGenerator([], columns.length);

  function handleRow(column: TransactionCompsColumn, columnLegend: string) {
    const rowRange = range(1, getArrayValue(tableDataTransactions).length + 1)
      .map(rowNumber => `${columnLegend}${rowNumber + 2}`)
      .toString()
      .replace(/"/g, '');

    return (row: TransactionCompsConfigurationRow, index: number) => {
      const indexesToIgnore = [
        0,
        weightedEquityValueTableRowNumber - 1,
        cashTableRowNumber - 1,
        debtTableRowNumber - 1,
        weightedEnterpriseValueTableRowNumber - 1,
      ];
      if (indexesToIgnore.includes(index)) {
        return;
      }
      const rowNumber = index + 1;
      const key = columnLegend + rowNumber;

      const multiplesAndWeightings = tableDataMultiplesAndWeightings[columnLegend];
      const selectionOrWeightingOrSpecifiedMultipleRow = [SELECTION, WEIGHTING, SELECTED_MULTIPLE].includes(row.alias);

      const tempValue = checkForTempData(
        column,
        row,
        tableDataTransactions,
        multiplesAndWeightings,
        selectionOrWeightingOrSpecifiedMultipleRow,
        sourceData.valuations_approach_gpt
      );

      const valueInColumn = getValue(column, row, sourceData.valuations_approach_gpt);
      const value = getValueToUse(valueInColumn, tempValue, selectionOrWeightingOrSpecifiedMultipleRow);
      const type = getValueByPropOrFallback(row, 'gridType', null);

      const expr = () => getExpr(row, columnLegend);

      let cell: TransactionCompsCell = {
        key,
        className: row.className,
        alias: getValueByPropOrFallback(row, 'alias', ''),
        value,
        columnId: getValueByPropOrFallback(column, 'row_ref', column.id) as string,
        parentColumn: column.parentColumn,
        columnLegend,
        columnOrder: column.order,
        rowNumber,
        isVisible: row.isVisible,
        displayNAforNull: row.displayNAforNull,
      };

      const inLTM = LTM_COLUMNS.indexOf(columnLegend) >= 0;
      const inMultipleCell = MULTIPLE_COLUMNS_LETTER.indexOf(columnLegend) >= 0;
      const existAliasAndIsNotTitle = row.alias !== 'title' && column[row.alias];

      cell = cellParser({
        cell,
        column,
        columns,
        columnLegend,
        existAliasAndIsNotTitle,
        expr,
        inLTM,
        inMultipleCell,
        row,
        type,
        value,
        rowNumber,
        rowRange,
        isDisabled,
      });

      if (
        cell.alias === COMPANY_ROW_ALIAS &&
        [LTM_EBITDA_MULTIPLE_ID, NTM_EBITDA_MULTIPLE_ID].includes(cell.columnId as string) &&
        useFinancialStatementAdjustedEbitda &&
        ebitdaOptions[cell.columnId as string]?.some(
          option => option.value !== ebitdaOptions[cell.columnId as string][0].value
        )
      ) {
        cell = {
          ...cell,
          readOnly: false,
          dataEditor: (props: any) =>
            GridSelect({
              ...props,
              options: ebitdaOptions[cell.columnId as string],
              handleOptionsNumberFormat,
            }),
          valueViewer: (props: any) => SelectValueViewer({ ...props, options: ebitdaOptions[cell.columnId as string] }),
          value: getEbitdaValueForViewer({
            cell: { columnId: cell.columnId as string },
            options: ebitdaOptions,
            valuation_approach_adjustments: sourceData.valuations_approach_gpt,
            is_transaction_comps: true,
          }),
        };
      }

      const cleanRow = () => {
        cell.expr = null;
        cell.gridType = 'string';
        cell.value = '';
      };
      const cleanLTMEBITDAMultipleLetter = () => {
        if (row.alias === MULTIPLE_DISCOUNT_ALIAS || row.alias === GPC_MULTIPLE_DISCOUNT_ALIAS) {
          cleanRow();
          cell.valueViewer = null;
          cell.dataEditor = null;
          cell.readOnly = true;
        }
        if (row.alias === GPT_SUMMARY) {
          cleanRow();
        }
      };
      const cleanNTMEBITDAMultipleLetter = () => {
        let selectedApproach = null;
        if (row.alias === GPC_MULTIPLE_DISCOUNT_ALIAS) {
          const { columnId } = cell;
          if (columnId === NTM_EBITDA_COLUMN_ID) {
            selectedApproach = ntmEbitdaGpcApproach ?? ntmEbitdaGpcReference;
            cell.alias = GPC_NTM_EBITDA_APPROACH_ALIAS;
            cell.value = selectedApproach;
          } else if (columnId === NTM_REVENUE_COLUMN_ID) {
            selectedApproach = ntmRevenueGpcApproach ?? ntmRevenueGpcReference;
            cell.alias = GPC_NTM_REVENUE_APPROACH_ALIAS;
            cell.value = selectedApproach;
          }
        }
        if (row.alias === MULTIPLE_DISCOUNT_ALIAS && NTM_MULTIPLES_IDS.includes(cell.columnId as string)) {
          let gpcApproach;
          let ltmMetric = '';
          let ntmMetric = '';
          if (columnLegend === NTM_REVENUE_MULTIPLE_LETTER) {
            ltmMetric = LTM_REVENUE_CUSTOM_KEY;
            ntmMetric = NTM_REVENUE_CUSTOM_KEY;
            gpcApproach = tableData.gpcApproaches?.find(
              approach =>
                approach?.valuations_approach_gpc?.id === ntmRevenueGpcApproach ||
                approach.panelId === ntmRevenueGpcReference
            );
          }
          if (columnLegend === NTM_EBITDA_MULTIPLE_LETTER) {
            ltmMetric = LTM_EBITDA_CUSTOM_KEY;
            ntmMetric = NTM_EBITDA_CUSTOM_KEY;
            gpcApproach = tableData.gpcApproaches?.find(
              approach =>
                approach?.valuations_approach_gpc?.id === ntmEbitdaGpcApproach ||
                approach.panelId === ntmEbitdaGpcReference
            );
          }

          const tableName = getApproachTableName({ approach: gpcApproach });
          const ntmDiscount = gpcApproach ? getNtmDiscount(tableName, ltmMetric, ntmMetric) : null;
          cell.expr = ntmDiscount;
        }
        if (row.alias === GPT_SUMMARY) {
          cleanRow();
        }
      };
      // Clean all Non GPT Companies cells
      if (!row.isGptRow && rowNumber > 2) {
        switch (columnLegend) {
          case CAP_IQ_TRANSACTION_ID_LETTER:
          case ACQUIRER_NAME_LETTER:
          case TRANSACTION_DATE_LETTER:
          case ENTERPRISE_VALUE_LETTER:
          case LTM_REVENUE_LETTER:
          case LTM_EBITDA_LETTER:
            cleanRow();
            break;
          case LTM_REVENUE_MULTIPLE_LETTER:
          case LTM_EBITDA_MULTIPLE_LETTER:
            cleanLTMEBITDAMultipleLetter();
            break;
          case NTM_REVENUE_MULTIPLE_LETTER:
          case NTM_EBITDA_MULTIPLE_LETTER:
            cleanNTMEBITDAMultipleLetter();
            break;
          default:
            break;
        }
      }

      handleMultipleCell(cell, column);

      cells = {
        ...cells,
        [key]: cell,
      };
    };
  }

  // Parse body cells
  columns.forEach((column, columnIndex) => {
    const columnLegend = alphabet[columnIndex];
    rowConfig.forEach(handleRow(column, columnLegend));
  });
  return cells;
};

export default parser;
